import { IconExpandMore, IconLogout, IconUser } from "@tine/designsystem-icons/sharp";
import { Button } from "@tine/designsystem-ui-react";
import { cn } from "@tine/designsystem-utils";
import { observer } from "mobx-react-lite";
import { type MouseEventHandler, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import NotificationsBadge from "../../account/notifications/NotificationsBadge";
import { sendInternalLinkClick } from "../../common/tracking";
import useMediaQuery from "../../common/useMediaQuery";
import { mediaQueries } from "../../common/utils";
import { useMenuItems } from "../../common/utils/menuItems";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/common/popover/Popover";
import authStore from "../../stores/auth/authStore";
import loginState from "../../stores/auth/loginState";
import contentStore from "../../stores/cms/contentStore";
import featuresStore from "../../stores/features/featuresStore";
import notificationsStore from "../../stores/notifications/notificationsStore";
import { getNotificationsTrackingText } from "../../stores/notifications/notificationsUtils";

const UserMenu = () => {
   const [_, redirectTo] = useLocation();
   const [show, setShow] = useState(false);
   const menuItems = useMenuItems();
   const buttonRef = useRef<HTMLButtonElement>(null);
   const isXl = useMediaQuery(mediaQueries.xl);

   const newPromotionArticlesCount = contentStore.getNewPromotionArticlesCount();
   const unreadNotificationsCount = notificationsStore.unreadNotifications.data.length;
   const totalNotificationsCount = unreadNotificationsCount + newPromotionArticlesCount;

   const activateSwitchCompany = (e: React.MouseEvent) => {
      loginState.transitionTo("COMPANY_SELECTION");
      e.preventDefault();
   };

   const showSwitchCompany = authStore.companies && authStore.companies.length > 1 && !authStore.isPunchoutSession();

   const logoutAndRedirect: MouseEventHandler = () => {
      if (authStore.isPunchoutSession()) {
         loginState.transitionTo("NOT_LOGGED_IN");
         window.location.replace("/");
         return;
      }
      redirectTo("/");
      loginState.transitionTo("NOT_LOGGED_IN");
   };

   const handleClick = () => {
      setShow(!show);
   };

   const handleButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === "Escape") {
         setShow(false);
      }
   };

   const listItemClasses = "px-5 py-3 hover:bg-surface-default-hover w-full text-start flex items-center";

   return (
      <>
         {featuresStore.notificationsEnabled && totalNotificationsCount > 0 && (
            <div className="absolute z-10 pointer-events-none -left-1 -top-2">
               <NotificationsBadge count={totalNotificationsCount} />
            </div>
         )}
         <Popover open={show} onOpenChange={setShow}>
            <PopoverTrigger asChild ref={buttonRef}>
               <Button
                  onKeyDown={handleButtonKeyDown}
                  onClick={handleClick}
                  aria-expanded={show}
                  aria-label="Min konto"
                  aria-controls="user-menu"
                  size={isXl ? "regular" : "iconOnly"}
                  iconPosition="left"
                  icon={<IconUser />}
                  variant="tertiary"
                  data-testid="user-menu-button"
               >
                  <div className="flex flex-row items-center">
                     <span className="text-nowrap">Min konto</span>
                     <span className="-mb-[2px] pl-1 text-xs">({authStore.currentCompany})</span>
                     <IconExpandMore className="w-6 h-6 -my-2 -me-2" />
                  </div>
               </Button>
            </PopoverTrigger>
            <PopoverContent>
               <ul
                  id="user-menu"
                  className="px-0 mb-0 text-sm"
                  onClick={() => {
                     buttonRef.current?.focus();
                     setShow(false);
                  }}
                  onKeyDown={(e) => {
                     if (e.key === "Escape") {
                        buttonRef.current?.focus();
                        setShow(false);
                     }
                  }}
               >
                  {showSwitchCompany && (
                     <li>
                        <button className="flex w-full" type="button" onClick={activateSwitchCompany}>
                           <div className={listItemClasses}>Bytt firma</div>
                        </button>
                     </li>
                  )}

                  <li aria-hidden="true" className="my-0 border-t border-ink-border-subtle" />
                  {menuItems.map((menuItem) => (
                     <li key={menuItem.name}>
                        <Link
                           to={menuItem.link}
                           className="hover:no-underline"
                           onClick={() =>
                              sendInternalLinkClick(
                                 menuItem.link,
                                 "User menu",
                                 getNotificationsTrackingText(menuItem.name, unreadNotificationsCount, newPromotionArticlesCount)
                              )
                           }
                        >
                           <div className={cn(listItemClasses, { "bg-surface-default-hover": menuItem.isActive })}>
                              <span>{menuItem.name}</span>
                              <span className="ml-1">
                                 {menuItem.name === "Varslinger" && <NotificationsBadge count={unreadNotificationsCount} />}
                              </span>
                              <span className="ml-1">
                                 {menuItem.name === "Kampanjer" && <NotificationsBadge count={newPromotionArticlesCount} />}
                              </span>
                           </div>
                        </Link>
                     </li>
                  ))}
                  <li aria-hidden="true" className="my-0 border-b border-ink-border-subtle" />

                  <li>
                     <button className="flex w-full" type="button" onClick={logoutAndRedirect}>
                        <div className={listItemClasses}>
                           <IconLogout className="ml-[-2.4px] h-[24px] w-[24px]" />
                           Logg ut
                        </div>
                     </button>
                  </li>
               </ul>
            </PopoverContent>
         </Popover>
      </>
   );
};

export default observer(UserMenu);
